import {
  Button,
  Flex,
  FormLabel,
  IconButton,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import { formatDateTime } from '../../../../helpers'
import { IKnowledgeArea } from '../../../../interfaces/KnowledgeArea/KnowledgeArea'
import { KnowledgeAreaService } from '../../../../services/KnowledgeArea/KnowledgeAreaServices'
import KnowledgeAreaDeleteModal from './KnowledgeAreaDeleteModal'
import { KnowledgeAreaFormModal } from './KnowledgeAreaModal'

export default function KnowledgeAreaPage() {
  const service = new KnowledgeAreaService()
  const toast = useToast()
  const [knowledgeArea, setKnowledgeArea] = useState<IKnowledgeArea[]>([])
  const [search, setSearch] = useState('')
  const [filtered, setFiltered] = useState<IKnowledgeArea[]>([])
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editing, setEditing] = useState<IKnowledgeArea | null>(null)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState<IKnowledgeArea | null>(null)
  const cancelRef = useRef(null)

  useEffect(() => {
    loadKnowledgeArea()
  }, [])

  const loadKnowledgeArea = async () => {
    const response = await service.list()
    setKnowledgeArea(response)
  }

  const handleDelete = async () => {
    if (!deleting) return

    try {
      await service.delete(deleting.id)

      toast({
        title: 'Área excluída',
        description: `A área "${deleting.name}" foi excluída com sucesso.`,
        status: 'success',
        duration: 3000,
        isClosable: true
      })

      setIsDeleteOpen(false)
      setDeleting(null)

      await loadKnowledgeArea()
    } catch (error) {
      toast({
        title: 'Erro ao excluir',
        description: 'Não foi possível excluir a área. Tente novamente.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }

  useEffect(() => {
    setFiltered(knowledgeArea.filter((area) => area.name.toLowerCase().includes(search.toLowerCase())))
  }, [search, knowledgeArea])

  return (
    <>
      <Flex flex="1" justifyContent="flex-end" alignItems="center" mb={4}>
        <FormLabel htmlFor="search" fontSize="sm" fontWeight="normal" p={0} m={0} mr={2}>
          Pesquisar
        </FormLabel>
        <Input
          type="search"
          size="sm"
          variant="outline"
          id="search"
          w="200px"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Flex>
      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Nome</Th>
              <Th>Criado em</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filtered.length === 0 ? (
              <Tr>
                <Td colSpan={4} textAlign="center">
                  Nenhuma área encontrada
                </Td>
              </Tr>
            ) : (
              filtered.map((area) => (
                <Tr key={area.id}>
                  <Td>{area.id}</Td>
                  <Td>{area.name}</Td>
                  <Td>{formatDateTime(area.created_at)}</Td>
                  <Td>
                    <Tooltip label="Editar">
                      <IconButton
                        aria-label="Editar"
                        icon={<i className="lni lni-pencil" />}
                        colorScheme="blue"
                        size="sm"
                        mr={2}
                        onClick={() => {
                          setEditing(area)
                          setIsFormOpen(true)
                        }}
                      />
                    </Tooltip>
                    <Tooltip label="Excluir">
                      <IconButton
                        aria-label="Excluir"
                        icon={<i className="lni lni-trash-can" />}
                        size="sm"
                        colorScheme="red"
                        onClick={() => {
                          setDeleting(area)
                          setIsDeleteOpen(true)
                        }}
                      />
                    </Tooltip>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <KnowledgeAreaFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        editing={editing}
        onSave={async (name: string, id?: number) => {
          if (id) {
            await service.update(id, { name })
          } else {
            await service.create({ name })
          }
          await loadKnowledgeArea()
        }}
      />
      <KnowledgeAreaDeleteModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onDelete={handleDelete}
        leastDestructiveRef={cancelRef}
        itemName={deleting?.name}
      />

      <Tooltip label="Adicionar área de conhecimento" aria-label="Adicionar área de conhecimento" placement="left">
        <Button
          colorScheme="magenta"
          position="fixed"
          bottom="20px"
          right="20px"
          borderRadius="full"
          width="70px"
          height="70px"
          onClick={() => {
            setEditing(null)
            setIsFormOpen(true)
          }}
        >
          <i className="lni lni-plus"></i>
        </Button>
      </Tooltip>
    </>
  )
}
